import { api } from '@/lib/api';
import Cookies from 'js-cookie';

// Types
export interface User {
  id?: number;
  name: string;
  phone: string;
  city?: string | null;
  birthdate?: string | null;
  avatar_url?: string | null;
  profile_completion?: number;
  role?: 'user' | 'admin' | 'manager';
  delivery_point_id?: number | null;
  telegram_id?: string | null;
  telegram_username?: string | null;
}

export interface LoginResponse {
  accessToken: string;
  user?: User;
  isNewUser?: boolean;
}

// Telegram Gateway (резервный метод)
export interface TelegramCodeResponse {
  success: boolean;
  requestId: string;
  message?: string;
  error?: string;
  code?: string; // код ошибки, например PHONE_NOT_REGISTERED
}

export interface VerifyTelegramCodeResponse {
  success: boolean;
  accessToken: string;
  isNewUser: boolean;
}

// Telegram Auth
export interface TelegramTokenResponse {
  success: boolean;
  token: string;
  botUsername: string;
}

export interface TelegramAuthStatus {
  success: boolean;
  completed?: boolean;
  accessToken?: string;
  isNewUser?: boolean;
  error?: string;
}

// Generate unique token for Telegram auth
function generateUniqueToken(): string {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
}

// Единая установка токена — раньше кука ставилась в семи местах без флагов.
// sameSite='lax' закрывает кросс-сайтовый POST (форма со стороннего сайта больше
// не унесёт JWT), но оставляет обычные переходы по ссылке на сайт рабочими.
// secure только на https: иначе локальная разработка по http не смогла бы войти.
function setAuthCookie(token: string): void {
  Cookies.set('accessToken', token, {
    expires: 7,
    path: '/',
    sameSite: 'lax',
    secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
  });
}

// VK ID Auth
export interface VkAuthResponse {
  success: boolean;
  accessToken: string;
  isNewUser: boolean;
  phoneRequired?: boolean;
}

// Auth API
export interface CallMeInitResponse {
  success: boolean;
  callmeNumber?: string;
  message?: string;
  error_code?: string;
  retryAfter?: number;
}

export interface CallMeCheckResponse {
  success: boolean;
  verified: boolean;
  waiting?: boolean;
  reason?: string;
  accessToken?: string;
  isNewUser?: boolean;
}

export const authApi = {
  // ===== Call Me (zvonok.com): юзер сам звонит на проверочный номер =====

  // Инициация: регистрирует ожидание входящего звонка, возвращает номер для набора.
  // Запасного платного звонка нет (решение владельца, 10.2026): при сбое окно входа
  // повторяет этот запрос само, см. use-auth-flow.ts.
  initiateCallMe: async (phone: string, signal?: AbortSignal): Promise<CallMeInitResponse> => {
    const response = await api.post<CallMeInitResponse>('/users/initiate-call-me', { phone }, { signal });
    return response.data;
  },

  // Поллинг статуса звонка. При подтверждении бэк сразу выдаёт accessToken.
  checkCallMe: async (phone: string, signal?: AbortSignal): Promise<CallMeCheckResponse> => {
    const response = await api.post<CallMeCheckResponse>('/users/callme-check', { phone }, { signal });
    if (response.data.accessToken && !signal?.aborted) {
      setAuthCookie(response.data.accessToken);
    }
    return response.data;
  },

  // ===== Telegram Gateway (резервный метод) =====
  
  // Отправить код через Telegram
  initiateTelegramCode: async (phone: string): Promise<TelegramCodeResponse> => {
    const response = await api.post<TelegramCodeResponse>('/users/initiate-telegram-code', { phone });
    return response.data;
  },

  // Проверить код из Telegram
  verifyTelegramCode: async (
    phone: string,
    requestId: string,
    code: string
  ): Promise<VerifyTelegramCodeResponse> => {
    const response = await api.post<VerifyTelegramCodeResponse>('/users/verify-telegram-code', {
      phone,
      requestId,
      code,
    });
    
    if (response.data.accessToken) {
      setAuthCookie(response.data.accessToken);
    }
    
    return response.data;
  },

  // ===== Telegram Auth =====
  
  // Generate and register Telegram auth token
  generateTelegramToken: async (): Promise<TelegramTokenResponse> => {
    // Generate unique token on frontend
    const token = generateUniqueToken();
    
    // Register token on backend
    await api.post('/users/generate-telegram-token', { token });
    
    // Get bot username from env or use default
    const botUsername = process.env.NEXT_PUBLIC_TELEGRAM_BOT_USERNAME || 'Aziatochkaru_bot';
    
    return {
      success: true,
      token,
      botUsername,
    };
  },

  // Check Telegram auth status
  checkTelegramAuth: async (token: string): Promise<TelegramAuthStatus> => {
    const response = await api.get<TelegramAuthStatus>(`/users/check-telegram-auth/${token}`);
    
    if (response.data.accessToken) {
      setAuthCookie(response.data.accessToken);
    }
    
    return response.data;
  },

  // ===== VK ID =====

  vkAuth: async (
    code: string,
    deviceId: string,
    codeVerifier: string,
    redirectUri: string
  ): Promise<VkAuthResponse> => {
    const response = await api.post<VkAuthResponse>('/users/vk-auth', {
      code,
      deviceId,
      codeVerifier,
      redirectUri,
    });

    if (response.data.accessToken) {
      setAuthCookie(response.data.accessToken);
    }

    return response.data;
  },

  // ===== Classic Login =====
  
  login: async (phone: string, password: string): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/users/login', { phone, password });
    
    if (response.data.accessToken) {
      setAuthCookie(response.data.accessToken);
    }
    
    return response.data;
  },

  // ===== Profile =====
  
  getProfile: async (signal?: AbortSignal): Promise<User> => {
    const response = await api.get<{ user: User; addresses: unknown[] }>('/profile', { signal });
    return response.data.user;
  },

  updateProfile: async (data: Partial<User>): Promise<User> => {
    await api.put<{ message: string; profileCompletion: number }>('/profile', data);
    // После обновления получаем актуальные данные
    const profileResponse = await api.get<{ user: User; addresses: unknown[] }>('/profile');
    return profileResponse.data.user;
  },

  // ===== Session =====
  
  refreshToken: async (): Promise<{ accessToken: string }> => {
    const response = await api.post<{ accessToken: string }>('/users/refresh');
    
    if (response.data.accessToken) {
      setAuthCookie(response.data.accessToken);
    }
    
    return response.data;
  },

  logout: async (): Promise<void> => {
    try {
      await api.post('/users/logout');
    } catch {
      // Ignore errors on logout
    } finally {
      Cookies.remove('accessToken');
    }
  },

  // Utils
  isAuthenticated: (): boolean => {
    return !!Cookies.get('accessToken');
  },

  getToken: (): string | undefined => {
    return Cookies.get('accessToken');
  },
};

export default authApi;
